import { contactRelevance, rankCaseStudies } from './scoring.js';
import { templateCrmCategory, templateScriptSection } from './role-routing.js';
import { commercialOutcomePoints, evidenceConfidence } from './performance-learning.js';
const clamp = (value) => Math.max(0, Math.min(100, Math.round(value)));
const positiveOutcomes = new Set(['reply-positive', 'meeting-booked', 'brief-received', 'project-won']);
const replyOutcomes = new Set(['reply-detected', 'reply-positive', 'reply-neutral', 'not-interested', 'out-of-office', 'meeting-booked', 'brief-received', 'project-won', 'not-now']);
export function whyNowSuggestions(opportunity) {
    if (!opportunity)
        return [];
    const out = [];
    const name = (opportunity.name || '').trim();
    const angle = (opportunity.recommendedAngle || '').trim();
    // Why Now copy is recipient-facing, so keep it conversational and light.
    // Opportunity research can be detailed internally; the email should usually
    // mention the trigger simply and leave the supporting detail out.
    if (opportunity.type === 'new-partnership' && name) {
        const withoutSuffix = name.replace(/\s+partnership\s*$/i, '').trim();
        const lead = (withoutSuffix.split(/\s+x\s+/i)[0] || withoutSuffix)
            .replace(/\s+(announces?|launches?)\b.*$/i, '')
            .trim();
        const label = lead || withoutSuffix || name;
        out.push(`The new ${label} partnership caught my eye, and I thought there could be a natural fit with the kind of creative support we provide.`);
        out.push(`I saw the new ${label} partnership and thought it was a good reason to get in touch.`);
    }
    if (opportunity.eventStart && name) {
        const d = new Date(opportunity.eventStart);
        if (!Number.isNaN(d.getTime())) {
            const month = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
            out.push(`I saw ${name} is coming up in ${month} and thought it was a good reason to get in touch.`);
            out.push(`With ${name} coming up in ${month}, I thought I’d get in touch as some of the work we’ve done in this space could be relevant.`);
        }
    }
    // For other opportunity types, use a short factual note only when it already
    // reads naturally. Avoid carrying internal planning language into the email.
    if (!out.length && angle) {
        const factual = angle.split(/(?<=[.!?])\s+/)[0].replace(/[.!?]+$/, '').trim();
        const internal = /\b(springboard|explicitly includes|direct opportunity|strong opportunity|target|activation rights|commercial trigger)\b/i.test(factual);
        if (factual && factual.length <= 120 && !internal) {
            const lower = factual.charAt(0).toLowerCase() + factual.slice(1);
            out.push(`I saw ${lower} and thought it was worth getting in touch.`);
        }
    }
    if (!out.length && name)
        out.push(`I saw ${name} and thought it was a good reason to get in touch.`);
    return [...new Set(out)].slice(0, 3);
}
export function rankCaseStudiesWithEvidence(contact, opportunity, library, sequences) {
    const base = rankCaseStudies(contact, opportunity, library);
    return base.map(item => {
        const uses = sequences.filter(s => !s.archived && !s.customOutreach && s.stage === 'sent' && (s.selectedCaseStudyIds || []).includes(item.id));
        const positive = uses.filter(s => positiveOutcomes.has(s.outcome)).length;
        const points = uses.reduce((sum, row) => sum + commercialOutcomePoints(row.outcome), 0);
        const avg = uses.length ? points / uses.length : 0;
        const evidenceBonus = uses.length >= 3 ? Math.min(8, Math.round(avg)) : 0;
        return { ...item, score: item.score + evidenceBonus, reasons: [...item.reasons, ...(evidenceBonus ? [`${uses.length} sent · ${evidenceConfidence(uses.length)} commercial evidence`] : [])], evidenceUses: uses.length, positiveUses: positive };
    }).sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}
/**
 * Choose a first-touch script using only comparable outreach in the same CRM
 * category / script section. Low-sample scripts are deliberately explored; once
 * each script has useful evidence, the higher genuine-human reply rate receives
 * most recommendations while a small challenger allocation keeps learning alive.
 */
export function recommendScriptForOutreach(templates, sequences, category, section) {
    const active = templates
        .filter(t => t.active && t.key !== 'follow-up' && templateCrmCategory(t) === category);
    const exact = active.filter(t => templateScriptSection(t) === section);
    const candidates = (exact.length ? exact : active)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }));
    if (!candidates.length)
        return undefined;
    const keys = new Set(candidates.map(t => t.key));
    const comparable = sequences.filter(s => !s.archived && !s.removedFromSentAt && !s.customOutreach && s.crmCategory === category && keys.has(s.scriptKey));
    const stats = candidates.map(template => {
        const rows = comparable.filter(s => s.scriptKey === template.key);
        const sentRows = rows.filter(s => s.stage === 'sent');
        const replies = sentRows.filter(s => s.replyClassification !== 'automatic' && s.outcome !== 'out-of-office' && replyOutcomes.has(s.outcome)).length;
        const positive = sentRows.filter(s => positiveOutcomes.has(s.outcome)).length;
        const planned = rows.filter(s => s.stage === 'sent' || s.stage === 'scheduled').length;
        const commercialPoints = sentRows.reduce((sum, row) => sum + commercialOutcomePoints(row.outcome), 0);
        return { templateKey: template.key, name: template.name, sent: sentRows.length, replies, positive, planned, replyRate: sentRows.length ? replies / sentRows.length : 0, commercialPoints, commercialAverage: sentRows.length ? commercialPoints / sentRows.length : 0 };
    });
    if (stats.length === 1)
        return { templateKey: stats[0].templateKey, mode: 'single', reason: 'Only active script in this category.', stats };
    const byLowUse = (a, b) => a.planned - b.planned || a.sent - b.sent || a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' });
    const underSampled = stats.filter(x => x.sent < 5).sort(byLowUse);
    if (underSampled.length) {
        const pick = underSampled[0];
        return { templateKey: pick.templateKey, mode: 'explore', reason: `Testing · needs more data · ${pick.sent}/5 sent`, stats };
    }
    const ranked = [...stats].sort((a, b) => b.commercialAverage - a.commercialAverage ||
        b.replyRate - a.replyRate ||
        (b.sent ? b.positive / b.sent : 0) - (a.sent ? a.positive / a.sent : 0) ||
        b.sent - a.sent || a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }));
    const best = ranked[0];
    const runner = ranked[1];
    const gap = best.replyRate - runner.replyRate;
    if (gap < 0.05) {
        const pick = [...stats].sort(byLowUse)[0];
        return { templateKey: pick.templateKey, mode: 'explore', reason: 'Testing · observed reply rates are still too close to call', stats };
    }
    const totalPlanned = stats.reduce((sum, x) => sum + x.planned, 0);
    const mature = stats.every(x => x.sent >= 15);
    const cycle = mature ? 5 : 4; // 80/20 after stronger evidence; 75/25 beforehand.
    const challengerTurn = totalPlanned % cycle === cycle - 1;
    if (challengerTurn) {
        const challenger = ranked.slice(1).sort(byLowUse)[0];
        return { templateKey: challenger.templateKey, mode: 'explore', reason: `Testing · challenger allocation · ${Math.round(challenger.replyRate * 100)}% human replies from ${challenger.sent} sent`, stats };
    }
    return { templateKey: best.templateKey, mode: 'perform', reason: `Recommended · strongest human reply rate / return signal · ${Math.round(best.replyRate * 100)}% replies from ${best.sent} sent`, stats };
}
export function outreachQualityScore(input) {
    const { contact, opportunity, caseStudies, personalisation, subject, body } = input;
    let score = 0;
    const reasons = [];
    const role = Math.round(contactRelevance(contact.title) * 0.35);
    score += role;
    reasons.push({ label: 'Role relevance', impact: role });
    if (opportunity) {
        score += 15;
        reasons.push({ label: 'Timely opportunity attached', impact: 15 });
    }
    if (personalisation.trim().length >= 20) {
        score += 16;
        reasons.push({ label: 'Specific why-now personalisation', impact: 16 });
    }
    else
        reasons.push({ label: 'Why-now personalisation is weak or missing', impact: -10 });
    const caseScore = caseStudies.length ? Math.min(16, 8 + caseStudies.length * 3) : 0;
    score += caseScore;
    if (caseScore)
        reasons.push({ label: `${caseStudies.length} proof point${caseStudies.length === 1 ? '' : 's'} selected`, impact: caseScore });
    else
        reasons.push({ label: 'No case-study proof selected', impact: -8 });
    const subjectScore = subject.trim().length >= 8 && subject.trim().length <= 70 ? 8 : 3;
    score += subjectScore;
    reasons.push({ label: 'Subject reviewed', impact: subjectScore });
    const bodyLength = body.trim().length;
    const bodyScore = bodyLength >= 180 && bodyLength <= 1200 ? 10 : bodyLength >= 80 ? 6 : 0;
    score += bodyScore;
    if (bodyScore)
        reasons.push({ label: 'Email length is usable', impact: bodyScore });
    if (contact.doNotContact) {
        score = 0;
        reasons.unshift({ label: 'Do Not Contact is active', impact: -100 });
    }
    const final = clamp(score);
    return { score: final, label: final >= 80 ? 'Strong' : final >= 65 ? 'Good with review' : final >= 50 ? 'Needs review' : 'Weak', reasons: reasons.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact)) };
}
export function nextActionForOutcome(outcome) {
    switch (outcome) {
        case 'reply-detected': return { label: 'Review reply', detail: 'Open Gmail, read the response and classify the commercial outcome before deciding what to send.', manualReply: true };
        case 'reply-positive': return { label: 'Reply personally', detail: 'Respond yourself and move toward a call, meeting or useful next step.', manualReply: true };
        case 'reply-neutral': return { label: 'Review + decide', detail: 'Read the reply and decide whether a personal response or a later follow-up is appropriate.', manualReply: true };
        case 'out-of-office': return { label: 'Follow-up rescheduled', detail: 'The threaded follow-up is scheduled for two business days after the recorded return date.', manualReply: false };
        case 'no-longer-at-company': return { label: 'Dead lead', detail: 'This person is suppressed from future outreach because they no longer work at the company.', manualReply: false };
        case 'bounced': return { label: 'Resolve email', detail: 'Find or verify an alternative address before any further outreach.', manualReply: false };
        case 'meeting-booked': return { label: 'Prepare meeting', detail: 'Use the CRM history and opportunity context to prepare for the conversation.', manualReply: false };
        case 'brief-received': return { label: 'Qualify brief', detail: 'Review scope, fit and next actions before progressing the opportunity.', manualReply: false };
        case 'project-won': return { label: 'Close the loop', detail: 'Keep the outreach attribution for performance learning; no further cold-outreach action is needed.', manualReply: false };
        case 'not-now': return { label: 'Set revisit timing', detail: 'Choose a future date based on the context rather than continuing the current sequence.', manualReply: false };
        case 'not-interested': return { label: 'Stop current outreach', detail: 'Do not continue this sequence. Revisit only if the context materially changes.', manualReply: false };
        case 'closed-no-response': return { label: 'Close sequence', detail: 'Leave closed and use the result in performance learning.', manualReply: false };
        default: return { label: 'Await response', detail: 'The approved follow-up/reply-detection workflow can continue. Any reply remains yours to handle.', manualReply: false };
    }
}
export function recommendedABTest(sequences, allowScript = false) {
    const tests = sequences.filter(s => !s.archived && (s.stage === 'sent' || s.stage === 'scheduled')).map(s => s.abTest).filter(Boolean);
    const counts = new Map();
    for (const test of tests)
        counts.set(test.dimension, (counts.get(test.dimension) || 0) + 1);
    const order = allowScript ? ['script', 'subject', 'opening', 'case-studies', 'send-time'] : ['subject', 'opening', 'case-studies', 'send-time'];
    const dimension = order.sort((a, b) => (counts.get(a) || 0) - (counts.get(b) || 0))[0];
    const title = dimension === 'script' ? 'Test one script against another' : dimension === 'subject' ? 'Test the subject line' : dimension === 'opening' ? 'Test the opening sentence' : dimension === 'case-studies' ? 'Test the proof / case-study mix' : 'Test send timing';
    const guidance = dimension === 'script'
        ? 'Choose two scripts from the same CRM category. Use comparable prospects and keep case studies and send timing as similar as practical.'
        : dimension === 'subject'
            ? 'Keep the audience, email body, proof and send window as similar as possible; change only the subject.'
            : dimension === 'opening'
                ? 'Keep the subject, proof and CTA the same; change only the first personalisation / opening sentence.'
                : dimension === 'case-studies'
                    ? 'Keep the copy and audience similar; vary only the selected proof points.'
                    : 'Keep the copy, audience and proof the same; vary only the send window.';
    return { dimension, title, guidance };
}
export function isReplyOutcome(outcome) { return replyOutcomes.has(outcome); }
export function isPositiveOutcome(outcome) { return positiveOutcomes.has(outcome); }
export const recommendABTest = recommendedABTest;
